'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { ChangeEvent, createRef, useEffect } from 'react';
import { Box, Burger, Center, Drawer, Group, Input, Menu } from '@mantine/core';
import { useDisclosure, useViewportSize } from '@mantine/hooks';
import { IconChevronDown, IconSearch } from '@tabler/icons-react';
import { Collapsed } from '@/app/items/components/colapsed/Collapsed';
import { menu } from '@/app/items/components/menu';
import { useTranslation } from '@/i18n/client';
import classes from './ItemsHeader.module.css';

export function ItemsHeader() {
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const { width } = useViewportSize();
  const pathname = usePathname();
  const router = useRouter();
  const { t } = useTranslation('items');
  const searchInput = createRef<HTMLInputElement>();

  useEffect(
    () => {
      if (drawerOpened) closeDrawer();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname],
  );

  useEffect(
    () => {
      if (drawerOpened && width > 1200) closeDrawer();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [width],
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && drawerOpened && window.innerWidth > 1200) {
      closeDrawer();
    }
  }, [closeDrawer, drawerOpened]);

  const onSubmit = (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (searchInput && searchInput.current && searchInput.current.value.length > 0) {
      router.push('/items/search/' + searchInput.current.value);
      searchInput.current.value = '';
    }
  };

  const items = menu.map((link) => {
    const menuItems = link.categories?.map((category) => (
      <Link key={category} href={`/items/${category}`} style={{ textDecoration: 'none' }}>
        <Menu.Item>{t(`categories.${category}`).toUpperCase()}</Menu.Item>
      </Link>
    ));

    if (menuItems) {
      return (
        <Menu key={link.label} trigger='hover' transitionProps={{ exitDuration: 0 }} withinPortal>
          <Menu.Target>
            <a
              href={link.link}
              className={classes.link}
              onClick={(event) => event.preventDefault()}
            >
              <Center>
                <span className={classes.linkLabel}>{t(`menu.${link.label}`).toUpperCase()}</span>
                <IconChevronDown size={16} stroke={1.5} className={classes.icon} />
              </Center>
            </a>
          </Menu.Target>
          <Menu.Dropdown>{menuItems}</Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <Link key={link.label} href={link.link} className={classes.link}>
        {t(`menu.${link.label}`).toUpperCase()}
      </Link>
    );
  });
  return (
    <Box className={classes.header}>
      <Box className={classes.inner}>
        <Burger opened={drawerOpened} onClick={toggleDrawer} size='sm' hiddenFrom='lg' />

        <Group gap={2} visibleFrom='lg'>
          {items}
        </Group>

        <Group ml={50} gap={3}>
          <form onSubmit={onSubmit}>
            <Input
              ref={searchInput}
              placeholder={t('search')}
              leftSection={<IconSearch size={16} stroke={1.5} />}
              minLength={1}
            />
          </form>
        </Group>

        <Drawer
          opened={drawerOpened}
          onClose={closeDrawer}
          padding='md'
          title={t('Navigation')}
          hiddenFrom='lg'
          zIndex={1000000}
        >
          <Collapsed />
        </Drawer>
      </Box>
    </Box>
  );
}
